import Phaser from 'phaser';
import { INK, hudText } from './text';

// Event feed: a column of short lines (joins, kills, notices) that fade out
// after a while, or earlier when the feed is full and a new line needs the
// room. Positioned by the owner (setX / top margin).
export interface FeedOptions {
  x: number;
  y: number;
  lines?: number; // max visible (default 6)
  ttlMs?: number; // fade after (default 30 s)
  fadeMs?: number; // fade duration (default 400 ms)
  fontSize?: number;
  lineHeight?: number;
}

interface FeedLine {
  text: Phaser.GameObjects.Text;
  bornAt: number;
  fading: boolean;
}

export class Feed {
  private readonly lines: FeedLine[] = [];
  private x: number;
  private readonly y: number;
  private readonly max: number;
  private readonly ttlMs: number;
  private readonly fadeMs: number;
  private readonly fontSize: number;
  private readonly lineHeight: number;

  constructor(
    private readonly scene: Phaser.Scene,
    opts: FeedOptions,
  ) {
    this.x = opts.x;
    this.y = opts.y;
    this.max = opts.lines ?? 6;
    this.ttlMs = opts.ttlMs ?? 30_000;
    this.fadeMs = opts.fadeMs ?? 400;
    this.fontSize = opts.fontSize ?? 14;
    this.lineHeight = opts.lineHeight ?? 20;
  }

  setX(x: number): void {
    this.x = x;
    this.reflow();
  }

  log(message: string, color: string = INK): void {
    const t = hudText(this.scene, this.x, 0, message, this.fontSize, color);
    this.lines.push({ text: t, bornAt: this.scene.time.now, fading: false });
    while (this.lines.length > this.max) this.retire(this.lines.shift()!);
    this.reflow();
  }

  // Call every frame: expires old lines.
  update(time: number): void {
    let changed = false;
    while (this.lines.length > 0 && time - this.lines[0]!.bornAt > this.ttlMs) {
      this.retire(this.lines.shift()!);
      changed = true;
    }
    if (changed) this.reflow();
  }

  // Current texts, oldest first (for tests and the debug line).
  texts(): string[] {
    return this.lines.map((l) => l.text.text);
  }

  // Fade out and drift up, then destroy; the remaining lines close the gap.
  private retire(line: FeedLine): void {
    if (line.fading) return;
    line.fading = true;
    const t = line.text;
    this.scene.tweens.add({ targets: t, alpha: 0, y: t.y - 10, duration: this.fadeMs, onComplete: () => t.destroy() });
  }

  private reflow(): void {
    let y = this.y;
    for (const line of this.lines) {
      line.text.setPosition(this.x, y);
      y += this.lineHeight;
    }
  }
}
